import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App.tsx'
import { initPostHog } from './lib/posthog'

// Initialise PostHog before the React tree so error tracking is active
// for the very first render. No-op when VITE_POSTHOG_KEY is not set.
initPostHog()

// Inject the per-tenant PWA manifest link BEFORE the React tree mounts,
// derived from the URL path (`/{tenantSlug}/...`). Chrome's "Add to
// Home Screen" and "Install app" flows decide whether to accept a
// manifest based on the DOM at the point the user triggers install; if
// we wait for `useMe` to resolve (which happens after auth), Chrome may
// already have snapshotted a manifest-less document and fall back to
// the letter-tile icon. `applyAppIcon` inside `ProtectedRoute` refreshes
// this link once real tenant data lands — this bootstrap step is a
// belt-and-braces for the install-before-navigation case.
try {
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0]
  // Skip auth/onboarding/etc. paths; those are handled by react-router,
  // not scoped to a tenant slug.
  const reserved = new Set(['auth', 'onboarding', 'admin', 'api'])
  if (firstSegment && !reserved.has(firstSegment)) {
    // Absolute API base URL — SPA and backend live on different
    // subdomains (`app.dev.…` vs `api.dev.…`). A relative path here
    // would resolve against the SPA host and land on nginx's SPA
    // fallback (`try_files … /index.html`), returning HTML instead of
    // the manifest JSON.
    const apiBase = import.meta.env.VITE_API_URL || ''
    const link = document.createElement('link')
    link.rel = 'manifest'
    link.href = `${apiBase}/api/tenants/${firstSegment}/manifest.webmanifest`
    // Cross-origin manifests need the `crossorigin` attribute; the
    // endpoint is public and cookie-less so `anonymous` is the correct
    // credentials mode.
    link.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(link)
  }
} catch {
  // Never let a manifest failure block the app from booting.
}

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Retry transient failures only. A 4xx is a decision the server already
      // made — repeating the request just doubles the load and the error
      // reporting for a result that cannot change (a 404 on an order the user
      // can't see was firing two identical alerts per page load).
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status
        if (typeof status === 'number' && status >= 400 && status < 500) return false
        return failureCount < 1
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
